/**
 * Due times on tasks (FOR-302).
 *
 * A task always has its date in `due_date` (YYYY-MM-DD). It may also be due at
 * a time: `due_at`, an ISO instant in UTC. The API keeps the two in step —
 * due_date is the date of due_at in the zone of whoever set it — so a client
 * that sends only `due_date` (a calendar drag, the old iOS app) keeps the time
 * of day.
 *
 * Times are picked and shown in the viewer's own timezone: the one on their
 * profile (Settings → Account, `user.timezone`), registered here by the auth
 * provider with setViewerTimezone(); until then, the browser's. Pure helpers
 * (Intl only), so tests/task-due.test.mjs can load them.
 */

const pad = (n: number) => String(n).padStart(2, "0")

type DueFields = { due_date?: string | null; due_at?: string | null }

export const DEFAULT_TIMEZONE = "America/New_York"

export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE
  } catch {
    return DEFAULT_TIMEZONE
  }
}

export function isValidTimezone(tz: string | null | undefined): tz is string {
  if (!tz) return false
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz })
    return true
  } catch {
    return false
  }
}

let viewerZone: string | null = null

/** Called by the auth provider with the signed-in person's profile timezone. */
export function setViewerTimezone(tz: string | null | undefined): void {
  viewerZone = isValidTimezone(tz) ? tz : null
}

/** The zone due times are read and shown in for the person using the app. */
export function viewerTimezone(): string {
  return viewerZone ?? browserTimezone()
}

type Parts = { year: number; month: number; day: number; hour: number; minute: number }

function zoneParts(date: Date, tz: string): Parts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  }).formatToParts(date)
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value)
  return { year: get("year"), month: get("month"), day: get("day"), hour: get("hour"), minute: get("minute") }
}

function parseInstant(dueAt: string | null | undefined): Date | null {
  if (!dueAt) return null
  const d = new Date(dueAt)
  return Number.isNaN(d.getTime()) ? null : d
}

/** yyyy-MM-dd of an instant in `tz`. */
export function dayIn(d: Date, tz: string = viewerTimezone()): string {
  const p = zoneParts(d, tz)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`
}

/** Today's yyyy-MM-dd in `tz`. */
export function todayIn(tz: string = viewerTimezone(), now: Date = new Date()): string {
  return dayIn(now, tz)
}

/**
 * The day the task is due, as the viewer reads it: the date of its due time
 * in their zone when it has one, otherwise its due_date.
 */
export function dueDay(task: DueFields, tz: string = viewerTimezone()): string | null {
  const at = parseInstant(task.due_at)
  if (at) return dayIn(at, tz)
  return task.due_date ? task.due_date.slice(0, 10) : null
}

/** "HH:mm" in `tz` for an <input type="time">, or "" without a time. */
export function timeInputValue(dueAt: string | null | undefined, tz: string = viewerTimezone()): string {
  const at = parseInstant(dueAt)
  if (!at) return ""
  const p = zoneParts(at, tz)
  return `${pad(p.hour)}:${pad(p.minute)}`
}

/** A wall-clock date (yyyy-MM-dd) and time (HH:mm) in `tz` → the ISO instant the API stores. */
export function dueAtFrom(day: string, time: string, tz: string = viewerTimezone()): string | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  const t = /^(\d{2}):(\d{2})$/.exec(time)
  if (!d || !t) return null
  const wall = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]))
  // The zone's offset at that moment (twice, so a DST change in between settles).
  const offsetAt = (ms: number) => {
    const p = zoneParts(new Date(ms), tz)
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - Math.floor(ms / 60_000) * 60_000
  }
  let utc = wall - offsetAt(wall)
  utc = wall - offsetAt(utc)
  return new Date(utc).toISOString()
}

/** "4:00 PM" in `tz`, or null without a time. */
export function dueTimeLabel(dueAt: string | null | undefined, tz: string = viewerTimezone()): string | null {
  const at = parseInstant(dueAt)
  if (!at) return null
  const { hour, minute } = zoneParts(at, tz)
  return `${hour % 12 === 0 ? 12 : hour % 12}:${pad(minute)} ${hour < 12 ? "AM" : "PM"}`
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/** "Oct 19, 2026" / "Oct 19, 2026, 4:00 PM" (withYear false drops the year). */
export function dueFullLabel(task: DueFields, withYear = true, tz: string = viewerTimezone()): string | null {
  const day = dueDay(task, tz)
  if (!day) return null
  const [y, m, d] = day.split("-").map(Number)
  const date = `${MONTHS[m - 1]} ${d}${withYear ? `, ${y}` : ""}`
  const time = dueTimeLabel(task.due_at, tz)
  return time ? `${date}, ${time}` : date
}

/**
 * The compact label a list row has room for (58px): "Today", "Tomorrow" or
 * "Oct 21" — and, for a task due at a time today, just the time ("4:00 PM"),
 * which is when the time matters. The full label goes in the row's tooltip.
 */
export function dueShortLabel(task: DueFields, today: string, tomorrow: string, tz: string = viewerTimezone()): string {
  const day = dueDay(task, tz)
  if (!day) return ""
  const time = dueTimeLabel(task.due_at, tz)
  if (day === today) return time ?? "Today"
  if (day === tomorrow) return "Tomorrow"
  const [, m, d] = day.split("-").map(Number)
  return `${MONTHS[m - 1]} ${d}`
}

/** Past due: a timed task once its time has passed, a dated one from the next day. */
export function isPastDue(task: DueFields, now: Date = new Date(), tz: string = viewerTimezone()): boolean {
  const at = parseInstant(task.due_at)
  if (at) return at.getTime() < now.getTime()
  const day = dueDay(task, tz)
  return day !== null && day < dayIn(now, tz)
}

/**
 * An activity diff value for a due field: "Oct 19, 2026" for due_date,
 * "Oct 19, 2026, 4:00 PM" for due_at, "none" when cleared. Null for any other
 * field, so the caller falls back to its own formatting.
 */
export function dueActivityValue(field: string, value: unknown, tz: string = viewerTimezone()): string | null {
  if (field !== "due_date" && field !== "due_at") return null
  if (value === null || value === undefined || value === "") return "none"
  if (typeof value !== "string") return String(value)
  return field === "due_at"
    ? dueFullLabel({ due_at: value }, true, tz) ?? value
    : dueFullLabel({ due_date: value.slice(0, 10) }, true, tz) ?? value
}

/** Zones offered in Settings → Account (plus the browser's and the current one). */
export const COMMON_TIMEZONES: { value: string; label: string }[] = [
  { value: "America/New_York", label: "Eastern (New York)" },
  { value: "America/Chicago", label: "Central (Chicago)" },
  { value: "America/Denver", label: "Mountain (Denver)" },
  { value: "America/Phoenix", label: "Arizona (Phoenix)" },
  { value: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
  { value: "America/Mexico_City", label: "Mexico City" },
  { value: "America/Bogota", label: "Bogotá" },
  { value: "America/Sao_Paulo", label: "São Paulo" },
  { value: "Europe/London", label: "London" },
  { value: "Europe/Madrid", label: "Madrid" },
  { value: "Asia/Manila", label: "Philippines (Manila)" },
  { value: "UTC", label: "UTC" },
]

// ── The due picker (src/components/tasks/due-picker.tsx) ──────────────────

/** "16:00" → "4:00 PM". */
export function timeLabel(time: string): string {
  const t = /^(\d{2}):(\d{2})$/.exec(time)
  if (!t) return ""
  const hour = Number(t[1])
  return `${hour % 12 === 0 ? 12 : hour % 12}:${t[2]} ${hour < 12 ? "AM" : "PM"}`
}

/** The time "End of day" picks. */
export const END_OF_DAY = "23:59"

/**
 * What someone typed into the time field → "HH:mm", or null when it isn't a
 * time. Understands "4pm", "4:30 pm", "4.30p", "16:00", "1630", "noon",
 * "midnight" and "eod". A bare hour with no am/pm reads as a working-day
 * time: 1 to 7 is the afternoon ("4:30" is 4:30 PM), 8 to 11 the morning,
 * and 0 or 13 to 23 (or a leading zero, "04:30") is a 24-hour time.
 */
export function parseTimeInput(input: string): string | null {
  const s = input.trim().toLowerCase().replace(/\s+/g, " ")
  if (!s) return null
  if (s === "noon" || s === "midday") return "12:00"
  if (s === "midnight") return "00:00"
  if (s === "eod" || s === "end of day") return END_OF_DAY

  const m = /^(\d{1,2})(?:[:.]?(\d{2}))? ?(a|am|a\.m\.?|p|pm|p\.m\.?)?$/.exec(s)
  if (!m) return null
  let hour = Number(m[1])
  const minute = m[2] === undefined ? 0 : Number(m[2])
  if (minute > 59) return null
  const meridiem = m[3]?.[0] as "a" | "p" | undefined

  if (meridiem) {
    if (hour < 1 || hour > 12) return null
    if (meridiem === "a") hour = hour === 12 ? 0 : hour
    else hour = hour === 12 ? 12 : hour + 12
  } else {
    if (hour > 23) return null
    const twentyFourHour = m[1].length === 2 && m[1].startsWith("0")
    if (!twentyFourHour && hour >= 1 && hour <= 7) hour += 12
  }
  return `${pad(hour)}:${pad(minute)}`
}

/** Every half hour of the day, "00:00" … "23:30", for the picker's list. */
export function halfHourSlots(): string[] {
  return Array.from({ length: 48 }, (_, i) => `${pad(Math.floor(i / 2))}:${i % 2 ? "30" : "00"}`)
}

/** The hours the picker's list shows until someone asks for all of them. */
export const DAY_HOURS = { from: "07:00", to: "21:00" } as const

/** A time inside DAY_HOURS (an empty time counts as inside). */
export function inDayHours(time: string): boolean {
  return !time || (time >= DAY_HOURS.from && time <= DAY_HOURS.to)
}

/** The list's half hours: 7 AM to 9 PM, or the whole day. */
export function timeSlots(allHours: boolean): string[] {
  const all = halfHourSlots()
  return allHours ? all : all.filter((s) => inDayHours(s))
}

/** The half-hour slot at or just before `time` (so the list can follow what's typed). */
export function nearestSlot(time: string): string {
  const t = /^(\d{2}):(\d{2})$/.exec(time)
  if (!t) return "09:00"
  return `${t[1]}:${Number(t[2]) >= 30 ? "30" : "00"}`
}

function addDays(day: string, n: number): string {
  const [y, m, d] = day.split("-").map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d + n))
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`
}

/** 0 = Sunday … 6 = Saturday, for a yyyy-MM-dd. */
function weekday(day: string): number {
  const [y, m, d] = day.split("-").map(Number)
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay()
}

/**
 * The picker's quick dates from `today`: Tomorrow, This Friday (today on a
 * Friday, the coming one otherwise) and Next Monday (always a later day).
 */
export function quickDueDays(today: string): { today: string; tomorrow: string; friday: string; monday: string } {
  const wd = weekday(today)
  return {
    today,
    tomorrow: addDays(today, 1),
    friday: addDays(today, (5 - wd + 7) % 7),
    monday: addDays(today, ((1 - wd + 7) % 7) || 7),
  }
}

/** "Oct 24", or "Oct 24, 2027" outside the current year. */
export function shortDayLabel(day: string, today: string): string {
  const [y, m, d] = day.split("-").map(Number)
  return `${MONTHS[m - 1]} ${d}${String(y) !== today.slice(0, 4) ? `, ${y}` : ""}`
}

/** The picker's pill, "Today at 4:00 PM", "Tomorrow", "Oct 24 at 9:00 AM" ("" without a date). */
export function duePickerLabel(day: string, time: string, today: string): string {
  if (!day) return ""
  const date = day === today ? "Today" : day === addDays(today, 1) ? "Tomorrow" : day === addDays(today, -1) ? "Yesterday" : shortDayLabel(day, today)
  return time ? `${date} at ${timeLabel(time)}` : date
}

/** "Eastern Time", the name of a zone for the picker's footer. */
export function timezoneName(tz: string = viewerTimezone(), at: Date = new Date()): string {
  if (tz === "UTC" || tz === "Etc/UTC") return "UTC"
  try {
    const name = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "longGeneric" })
      .formatToParts(at).find((p) => p.type === "timeZoneName")?.value
    if (name && !name.startsWith("GMT")) return name
  } catch {
    // Fall through to the zone's id.
  }
  return tz.split("/").pop()?.replace(/_/g, " ") ?? tz
}

/**
 * The /tasks "Due today" strip: open tasks due today and those already past
 * due, as the viewer's day reads. A task due at a time that has passed today
 * counts as overdue. Each list is soonest first; tasks with no due date drop.
 */
export function dueNow<T extends DueFields>(
  tasks: T[],
  tz: string = viewerTimezone(),
  now: Date = new Date(),
): { overdue: T[]; today: T[] } {
  const today = todayIn(tz, now)
  // A date without a time is due by the end of that day where the viewer is.
  const when = (t: T) => parseInstant(t.due_at)?.getTime() ?? Date.parse(dueAtFrom(dueDay(t, tz) ?? today, "23:59", tz) ?? "")
  const overdue: T[] = []
  const due: T[] = []
  for (const t of tasks) {
    const day = dueDay(t, tz)
    if (!day || day > today) continue
    const at = parseInstant(t.due_at)
    if (day < today || (at && at.getTime() < now.getTime())) overdue.push(t)
    else due.push(t)
  }
  const bySoonest = (a: T, b: T) => when(a) - when(b)
  return { overdue: overdue.sort(bySoonest), today: due.sort(bySoonest) }
}
